export const en = {
  nav: {
    by: "by codemenschen.at",
    ideas: "Apps",
    create: "Your own idea",
    how: "How it works",
    pricing: "Pricing",
    faq: "FAQ",
    cta: "Browse apps",
    account: "My projects",
    login: "Sign in",
  },
  hero: {
    eyebrow: "App development · Made in Vienna",
    titleA: "Your app.",
    titleB: "You own it.",
    lede: "Pick a ready-made app idea or describe your own. You see a fixed price up front, pay online, and our AI factory builds, tests and delivers the finished app, with the code and all rights included.",
    ctaIdeas: "Browse app ideas",
    ctaCreate: "Describe your own idea",
    chips: [
      "Fixed price, shown before you pay",
      "Code and rights are yours",
      "Stripe checkout, refund if we cannot deliver",
    ],
  },
  stats: {
    apps: "app ideas ready to build",
    price: "smallest fixed price",
    ownN: "100%",
    own: "of the code and the rights go to you",
  },
  how: {
    eyebrow: "How it works",
    title: "Four steps.",
    cta: "See the app ideas →",
    steps: [
      { h: "Choose or describe", p: "Pick an idea from the catalog or describe your own app in plain words." },
      { h: "Fixed price up front", p: "You see the full scope, the price and the delivery estimate before you pay a cent." },
      { h: "We build and test", p: "Our AI development pipeline builds your app; humans review every release before it ships." },
      { h: "You own everything", p: "Code, app and store listing are yours, published under your own developer account." },
    ],
  },
  split: {
    eyebrow: "Division of labor",
    title: "We build the software. You make the decisions.",
    weH: "What we do",
    we: [
      "Design, code and test every feature",
      "Run the build pipeline and every release",
      "Hand over the code, the repository and the store listing",
      "Prepare the store assets and, if you want, the campaigns",
    ],
    youH: "What you do",
    you: [
      "Choose an idea or describe your own app",
      "Approve scope and fixed price before you pay",
      "Review the preview build and request fixes",
      "Decide when to publish and when to advertise",
    ],
    note: "You do not need to hire developers or run servers. The fixed price covers that.",
  },
  ideas: {
    eyebrow: "Available app ideas",
    title: "Every idea shows price and scope up front.",
    lede: "Selected by our AI analysis of app-store data: real complaints, real gaps. Every idea is built exclusively for one customer. Once it is taken, it is gone.",
    note: "⚠ All market figures are AI-generated estimates from public sources, not measurements.",
    from: "Fixed price",
    delivery: "Delivery estimate",
    type: "App type",
    built: "Built for a customer",
    view: "View idea →",
  },
  createBanner: {
    title: "Your own idea?",
    p: "Describe your app in a few clicks. You get an instant price estimate and the full scope before checkout.",
    cta: "Start the app wizard →",
  },
  honest: {
    eyebrow: "Before you buy",
    title: "What you should know.",
    items: [
      { h: "Delivery times are estimates.", p: "The build pipeline is fast, but app-store review times are outside our control. We show estimates, not promises." },
      { h: "Connected apps have a monthly fee.", p: "Apps that need accounts, sync or a database run on our servers and carry a monthly hosting and maintenance fee, shown before checkout, never after." },
      { h: "If the build fails, you get your money back.", p: "If our pipeline plus our human developers cannot deliver the agreed scope, we refund the development price. That is the deal." },
      { h: "Publishing runs under your accounts.", p: "Apple and Google require apps like these to be published under your own developer account (Apple $99/year, Google $25 once). We set it up with you." },
    ],
  },
  pricing: {
    eyebrow: "Pricing",
    title: "Every euro, accounted for.",
    note: "There are no other fees. Advertising budget is always separate from our fees and is spent 1:1 on ads.",
    items: [
      { h: "App development", fig: "€149–1,500", p: "Fixed price, shown with the full scope before checkout." },
      { h: "Store publishing", fig: "€79 one-time", p: "Optional. Icon, screenshots, listing text and the submission." },
      { h: "Developer-account setup", fig: "€49 one-time", p: "Optional. We set up your Apple and Google accounts together with you." },
      { h: "Launch marketing", fig: "from €129", p: "Optional. Campaign concept, ad copy and the creatives." },
      { h: "Hosting & maintenance", fig: "€0 or monthly", p: "Local apps cost nothing. Connected apps carry a monthly fee, shown before checkout." },
      { h: "Advertising budget", fig: "Your amount", p: "Optional. Spent 1:1 on ads, always separate from our fees." },
    ],
  },
  about: {
    eyebrow: "Who builds this",
    title: "A real company in Vienna, not a landing page.",
    p: "Built and operated by codemenschen.at, a software company in Vienna. Look us up, read the Impressum, and you know exactly who is accountable.",
    cta: "Visit codemenschen.at ↗",
  },
  faq: {
    eyebrow: "FAQ",
    title: "The questions you should be asking.",
    cta: "Questions answered? See the ideas →",
    items: [
      { q: "Do I really own the app?", a: "Yes. You commission the development; the finished app, its code and its store listing belong to you." },
      { q: "What if I don't like the result?", a: "You review a preview build before release and can request fixes within the agreed scope. If we cannot deliver the scope, you get a refund." },
      { q: "Can you also market the app?", a: "Yes. An optional launch-marketing package plus a separate ad budget that is spent 1:1 on campaigns." },
    ],
  },
  final: {
    title: "Your app is waiting.",
    lede: "Ready-made ideas with a fixed price and full scope, or your own idea in the wizard.",
    cta1: "Browse app ideas →",
    cta2: "Describe your own idea",
  },
  bar: {
    title: "Your app is waiting.",
    sub: "fixed price, shown before you pay",
    cta: "Browse app ideas →",
  },
  detail: {
    price: "Fixed development price",
    hosting: "Hosting & maintenance",
    hostingA: "€0/month, runs fully on the device",
    weeks: "Delivery estimate",
    weeksUnit: "weeks",
    typeA: "Local app",
    typeB: "Connected app",
    cta: "Have this app built",
    ctaTaken: "Already built for a customer",
    why: "Why this idea",
    market: "Market snapshot",
    aud: "Who it's for",
    sample: "Sample listing, AI-estimated data",
    back: "← All app ideas",
    estimateNote: "All figures are AI-generated estimates from public sources and marked as such until real data exists.",
  },
  wizard: {
    title: "Describe your app",
    lede: "Four quick questions. The price updates live. You only pay after you've seen the full scope.",
    idea: "What should the app do?",
    ideaPh: "e.g. A booking app for my dance school with class schedules and payments…",
    ideaExamplesLabel: "Examples:",
    ideaExamples: [
      "Appointment booking for my hair salon: customers pick a time and service and get a push reminder.",
      "Packing list for trips: create lists, tick off items, works offline.",
      "Tip calculator: enter the bill, pick a percentage, split between people.",
    ],
    ideaHint: "Tip: who uses the app, what does it do, and which three things can a user do in it?",
    hintsLabel: "From your description:",
    hintsDismiss: "hide",
    refine: "✨ Sharpen my idea",
    refining: "Sharpening…",
    refineNote: "AI suggestion. You decide what to keep. Up to 3 rounds per idea.",
    refineTitle: "Suggestion",
    refineAccept: "Use this description",
    refineAccepted: "Applied ✓",
    refineQuestions: "Quick check:",
    refineFeatures: "Add features:",
    refineAgain: "Sharpen again with these answers",
    refineLimit: "Today's limit for the assistant is reached. You can of course keep editing the description yourself.",
    refineUnavailable: "The assistant is not reachable right now. Just write the description yourself. That is all we need.",
    refineOffTopic: "That does not sound like an app idea. Describe what the app should do, and for whom.",
    audience: "Who is it for?",
    audOpts: { consumer: "Consumers", b2b: "Businesses", both: "Both" },
    platform: "Where should it run?",
    platOpts: { web: "Web app", mobile: "Mobile app", both: "Web + mobile" },
    features: "Which features does it need?",
    featureLabels: {
      auth: "User accounts / login",
      pay: "Payments",
      dash: "Dashboard / statistics",
      ai: "AI features",
      notif: "Push notifications",
      api: "External integrations",
      offline: "Offline capability",
      i18n: "Multi-language",
    } as Record<string, string>,
    estTitle: "Your estimate",
    estMissing: "Still missing:",
    estPrice: "Fixed development price",
    estWeeks: "Delivery estimate",
    estType: "App type",
    estHosting: "Hosting & maintenance",
    estHostingA: "€0/month (local app)",
    estNote: "The final scope is confirmed before checkout. Connected apps (accounts, sync, database) run on our servers for the monthly fee shown.",
    cta: "Continue to scope & checkout",
  },
  checkout: {
    title: "Checkout",
    summary: "Your order",
    development: "App development",
    packagesTitle: "Optional packages",
    packages: {
      storePublishing: "Store publishing package",
      transferAssist: "Developer-account setup help",
      marketingLaunch: "Launch marketing package",
    } as Record<string, string>,
    storeLocalesTitle: "Store-listing languages",
    storeLocalesNote: "App name, subtitle, description, keywords and release notes are generated for each language you tick.",
    localeNames: { de: "German", en: "English" } as Record<string, string>,
    adBudget: "Monthly ad budget (optional)",
    adBudgetNote: "Billed separately after campaign approval, spent 1:1 on ads and never part of today's payment.",
    adNone: "No ads for now",
    email: "Your e-mail",
    step1: "1 · What you get",
    step2: "2 · Ad budget",
    step3: "3 · Build start and withdrawal",
    step4: "4 · E-mail and terms",
    termsTitle: "Terms",
    termsLabel: "I have read the terms and the withdrawal information, and I agree to them.",
    termsLink: "Read the terms",
    withdrawalLink: "Withdrawal information",
    waiverTitle: "Build start & right of withdrawal",
    waiverLabel: "I expressly request that work starts immediately, and I acknowledge that I lose my right of withdrawal once performance has begun.",
    waiverOff: "Without this consent your build starts after the 14-day withdrawal period. Your withdrawal right stays fully intact.",
    hostingLine: "Hosting & maintenance (monthly, from delivery)",
    totalToday: "Due today",
    firstYear: "Total in the first 12 months",
    pay: "Pay securely with Stripe →",
    working: "Preparing checkout…",
    staging: "⚠ Payment is not connected yet (staging). Your request was saved. We'll contact you by e-mail.",
    missingQuote: "No quote found. Start from an app idea or the wizard.",
    errors: { generic: "Something went wrong. Please try again." },
  },
  success: {
    title: "Thank you, payment received.",
    p: "Your project has been created. You'll get an e-mail with a sign-in link to your project dashboard; the build pipeline picks your app up from there.",
    cta: "Go to your projects",
  },
  project: {
    timeline: "Pipeline",
    criteria: "Acceptance criteria",
    builds: "Builds",
    tabs: { app: "App", store: "Store", marketing: "Marketing", activity: "Activity" },
    approveTitle: "Approval",
    buildsPending: "No build yet. The factory is working.",
    criteriaSummary: "{passed} of {total} acceptance criteria passed",
    assetsEmpty: "Store texts are generated after your approval.",
    download: "Download",
    platformNames: { bundle: "Source code (tar.gz)", android: "Android (.apk)", ios: "iOS", web: "Web preview" } as Record<string, string>,
    openPreview: "Open in browser →",
    previewHint: "The web preview runs the app in your browser, nothing to install. The installable Android app (.apk) is built after your approval.",
    previewTitle: "Live preview",
    previewEmpty: "Your app appears here as soon as the factory has built the first preview.",
    previewReload: "Reload",
    previewOpen: "Open ↗",
    buildRunning: "Android build running. The .apk shows up here as soon as it is done (queue up to ~30 minutes).",
    buildFailed: "The Android build failed. Our developers have been notified and will build it by hand. The web preview and the source code stay available.",
    sourceHint: "The source code is yours, download it any time. Need help moving it into your own accounts? That's the developer-account setup package.",
    approve: "Approve preview → build app",
    approveHint: "You're happy with the web preview: the project becomes READY, store texts are written and the Android build (.apk) starts. Changes are cheaper before this step: every build costs time and quota.",
    approved: "Approved. The project is READY.",
    failed: "The pipeline could not finish. Our developers have been notified and will take over.",
    changesTitle: "Request changes",
    changesHint: "Not quite right? Describe what to change. The app is adjusted, re-tested and a new preview is built. {left} of {max} free rounds left.",
    changesPh: "e.g. The primary button should be green, and the start screen should show today's date.",
    changesCareHint: "Describe what should be different. The app is adjusted, re-tested and a new preview is built. A released app stays online meanwhile.",
    careTitle: "Appwerk Care",
    careOffer: "Or {price}/month: unlimited changes, cancel any time, ends with the billing month.",
    careBenefits: [
      "Unlimited change rounds: fixes, texts, layout, small adjustments",
      "Updates for new iOS and Android versions",
      "Monthly, no minimum term",
    ],
    careStart: "Start Care for {price}/month →",
    careActiveHint: "active, unlimited changes included.",
    careEndsOn: "Care ends on {date}.",
    careCancel: "Cancel Care",
    careCancelConfirm: "Cancel Care at the end of the billing month? Changes stay included until then.",
    carePastDue: "The last Care payment failed. Please update the payment method via the Stripe link in the e-mail. Until then the regular conditions apply.",
    crHint: "The more concrete, the better the result: which screen, which element, how is it now, how should it be?",
    crRefine: "✨ Sharpen my request",
    crRefining: "Sharpening…",
    crRefineNote: "Checks before you order whether the request fits a change round. Up to 3 rounds.",
    crRefineAccept: "Use this wording",
    crRefineAccepted: "Applied ✓",
    crRefineQuestions: "Quick check:",
    crRefineAgain: "Sharpen again with these answers",
    crRefineOutOfScope: "Does not fit a change round:",
    crRefineLimit: "Today's limit for the assistant is reached. You can of course keep writing the request yourself.",
    crRefineUnavailable: "The assistant is not reachable right now. Just write the request yourself.",
    crRefineOffTopic: "That does not sound like a change to the app. Describe what should be different.",
    changesSend: "Send change request",
    changesPaidHint: "Free rounds used up or app already released: a change round costs {price} (one-time). Work starts right after payment and ends with a new preview for your approval. A released app stays published meanwhile.",
    changesPay: "Pay {price} & order the change →",
    changesPayLink: "Complete payment →",
    changesInProgress: "We're working on your change request. This page refreshes automatically.",
    changesHistory: "Change requests",
    changesRound: "Round",
    crStatus: {
      awaiting_payment: "Awaiting payment",
      paid: "Paid, queued",
      in_progress: "In progress",
      done: "Done, new preview built",
      out_of_scope: "Outside the ordered scope",
      failed: "Could not be applied, we've been notified",
    } as Record<string, string>,
    back: "← All projects",
    signInNeeded: "You are not signed in on this device. Request a sign-in link. Afterwards you land right back on this project.",
    signIn: "Request sign-in link",
    notFound: "This project could not be loaded. It belongs to another account, or your session has expired. Please sign in again.",
    open: "Details",
    assets: "Store assets",
    assetsHint: "Generated automatically in the store-listing languages you ordered. Review before publishing.",
    kinds: {
      name: "App name",
      subtitle: "Subtitle",
      description: "Description",
      keywords: "Keywords",
      release_notes: "Release notes",
    } as Record<string, string>,
    publishing: "Store publishing",
    publishingHint: "Apple and Google require apps like these to be published under your own developer account (Apple $99/year, Google $25 once). Start publishing, create the accounts with our guide, and we handle the submission.",
    publishStart: "Start publishing",
    stores: { apple: "Apple App Store", google: "Google Play" } as Record<string, string>,
    accountPh: "Your developer-account ID / team name",
    accountSave: "Save",
    subStatus: {
      waiting_account: "Waiting for your developer account",
      preparing: "We're preparing the submission",
      submitted: "Submitted to the store",
      in_review: "In store review",
      live: "Live in the store 🎉",
      rejected: "Rejected, we're on it",
    } as Record<string, string>,
    marketing: "Marketing",
    marketingHint: "Your launch-marketing package: we generate campaign strategy, ad copy and landing copy for Google and Meta. Nothing is published and no budget is spent before your approval, and ads themselves go live in a later release.",
    marketingGenerate: "Generate campaign drafts",
    marketingGenerating: "Generating, this page refreshes automatically…",
    campaignApprove: "Approve content",
    campaignReject: "Reject",
    campaignStatus: {
      pending_approval: "Awaiting your approval",
      approved: "Content approved",
      rejected: "Rejected",
      live: "Live",
      ended: "Ended",
    } as Record<string, string>,
    creativeKinds: {
      headline: "Headline",
      ad_copy: "Ad copy",
      landing: "Landing copy",
      image_prompt: "Image brief",
    } as Record<string, string>,
  },
  admin: {
    title: "Workshop",
    loading: "Loading…",
    noAccess: "This account has no access.",
    goSignIn: "Go to sign-in",
    empty: "Nothing here.",
    libSearchHint: "Search the captions",
    libShared: "Shared",
    libShare: "Share",
    libUnshare: "Unshare",
    libDelete: "Delete",
    libDeleteConfirm: "Delete this photo for good?",
    libOn: "Library on",
    libOff: "Library off",
    libOffNote:
      "The library is off. Every ad gets a freshly generated picture. Collecting carries on anyway.",
    libToggleHint: "Off means: do not search, generate instead.",
    libShareHint: "A shared photo may go to a different customer too.",
    libCaptionHint: "Click to edit. This text is what gets searched.",
    refNote:
      "Collected app screens, kept to look things up. They are never shipped. The prototype writer reads the rules distilled from them, not the pictures.",
    refUnavailable: "The library is not mounted on this server.",
    refSearchHint: "Search notes, button text or source",
    refMedium: "Medium",
    refScreenType: "Screen",
    refIndustry: "Industry",
    refPattern: "Pattern",
    refScheme: "Scheme",
    refGrade: "Resolution",
    refLabelState: "Labelled",
    refAll: "All",
    refYes: "yes",
    refNo: "no",
    refCount: "{matched} of {total} images, {labelled} labelled",
    refReset: "Clear filters",
    refPrev: "Back",
    refNext: "Next",
    refPage: "Page {page} of {pages}",
    refOpen: "Open original",
    tabs: {
      overview: "Overview",
      projects: "Projects",
      ads: "Ads",
      customers: "Customers",
      prototypes: "Prototypes",
      library: "Photo library",
      references: "References",
    },
    protoWhen: "When",
    protoKind: "Kind",
    protoStatus: "Status",
    protoPrompt: "What was typed",
    protoTook: "Took",
    protoQa: "QA",
    protoOpen: "Open",
    protoExpired: "expired",
    protoRepairs: "{n} repairs",
    protoEmpty: "No prototype yet.",
    projectsTile: "Projects",
    runsTile: "Pipeline runs",
    connectionsTile: "Ad accounts",
    connected: "connected",
    notConnected: "not connected",
    missing: "missing",
    connectionsHint: "Live credential check: factory:ads-check on the server.",
    adsTile: "Ads",
    revenueTile: "Revenue",
    customersTile: "Customers",
    queued: "Queued",
    running: "Running",
    failed24h: "Failed (24 h)",
    paidOrders: "Paid orders",
    paidEur: "Paid",
    hosting: "Hosting per month",
    orders: "Orders",
    attention: "Needs attention",
    allQuiet: "Nothing open. Everything is running.",
    kinds: {
      project_failed: "Project failed",
      run_failed: "Stage failed",
      run_stalled: "Stage stalled",
      ad_failed: "Ad failed",
      prototype_qa: "Prototype shipped with faults",
    },
    retryStage: "Run stage again",
    rerender: "Render again",
    stageQueued: "Stage queued",
    statusForced: "Status set",
    adQueued: "The ad is being rendered again.",
    search: "Search",
    searchHint: "Project name or e-mail",
    allStatuses: "All statuses",
    project: "Project",
    status: "Status",
    customer: "Customer",
    order: "Order",
    open: "Open",
    runStage: "Run stage",
    forceStatus: "Set status",
    runs: "Runs",
    events: "History",
    ad: "Ad",
  },
  lab: {
    title: "Ads",
    intro: "Ads generated on the server. Not public yet.",
    signIn: "Sign in to see the clips.",
    goSignIn: "Go to sign-in",
    empty: "No ads yet.",
    loading: "Loading…",
    failed: "Could not load the clip.",
    background: "Background",
    bgAuto: "Automatic",
    bgSite: "Screenshot of the site",
    bgPhoto: "AI photo",
    kind: "Kind",
    kindVideo: "Video ad",
    kindImage: "Image ad",
    open: "View",
    createTitle: "Create a new ad",
    promptLabel: "Describe the clip in one sentence",
    promptHint: "For example: a short Instagram clip showing that customers can book an appointment in ten seconds.",
    project: "Project",
    format: "Format",
    vertical: "Vertical",
    square: "Square",
    landscape: "Landscape",
    formats: {
      vertical: "Story / Reels",
      square: "Feed square",
      landscape: "Wide / YouTube",
      feed_portrait: "Feed portrait (4:5)",
      link: "Link ad (1.91:1)",
      gads_square: "Google square",
      gads_portrait: "Google portrait (4:5)",
    },
    angle: "Story angle",
    angleAuto: "Automatic",
    angles: {
      problem_solution: "Problem and solution",
      before_after: "Before and after",
      founder: "The person behind it",
      testimonial: "From the customer's side",
      demo: "How it works",
      price_anchor: "What it replaces",
      seasonal: "Seasonal moment",
    },
    goal: "Goal",
    goalAuto: "Automatic",
    goals: {
      booking: "Book an appointment",
      call: "Call or write",
      quote: "Ask for a quote",
      buy: "Buy the product",
      signup: "Sign up",
      visit: "Visit the website",
    },
    create: "Create ad",
    queued: "Queued",
    rendering: "Rendering, this takes a few minutes",
    failedState: "Failed",
    busy: "A render is already running for this project.",
    play: "Play",
  },
  account: {
    title: "Your projects",
    emailPrompt: "Enter the e-mail you ordered with. We'll send you a sign-in link.",
    send: "Send sign-in link",
    sent: "If that address has an account, a sign-in link is on its way (valid 30 minutes).",
    empty: "No projects yet.",
    buildStarts: "Build starts",
    hosting: "Hosting/month",
    total: "Order total",
    logout: "Sign out",
  },
  ads: {
    campaignsTitle: "Campaigns",
    campaignsIntro: "Ads on Codemenschen's ad accounts. Created paused; you switch them on.",
    noCampaigns: "No campaigns yet.",
    platformOff: "not connected",
    publish: "Create paused",
    activate: "Switch on (spend starts)",
    pauseAd: "Pause",
    stUnpublished: "not created",
    stPublishing: "creating",
    stPaused: "paused, ready",
    stActive: "running",
    stFailed: "failed",
    problems: "To fix",
  },
  proto: {
    navLink: "Try a prototype",
    title: "A prototype in one minute",
    lead: "Describe your idea in one sentence. We build you a clickable preview right away, free.",
    label: "What do you want to build?",
    hint: "For example: a landing page for a hair salon with online booking and a price list.",
    kindLabel: "What should we show you?",
    kinds: { site: "Website", app: "App", ads: "Ads" },
    kindHints: {
      site: "A clickable home page with everything a customer wants before they call.",
      app: "Four screens of your app on a phone, the way it would actually be used.",
      ads: "Five finished ads in the sizes Meta and Google really run.",
    },
    hints: {
      site: "For example: a home page for a hair salon in Vienna with a price list and online booking.",
      app: "For example: an app where regulars book and move their own salon appointment.",
      ads: "For example: ads for a hair salon in Vienna that bring new customers to online booking.",
    },
    go: "Build prototype",
    building: "Building, one moment…",
    failed: "That did not work. Try again.",
    expired: "This prototype has expired.",
    shareTitle: "Your prototype",
    shareHint: "This preview is live for a few days. Share the link, or let us turn it into a real app.",
    makeReal: "Turn it into a real app",
    another: "Build another",
    limit: "Daily limit reached. Come back tomorrow or get in touch.",
    tooLong: "That is more than {max} characters. Shorten it a little and try again.",
    mine: "Your prototypes",
    mineHint: "Kept on this device only. We store nothing for it and send nothing away.",
    daysLeft: "{n} days left",
    oneDayLeft: "1 day left",
    forget: "Remove",
    stages: {
      studying: "Studying the best of the trade before drawing.",
      writing: "Writing the page. This takes two to four minutes.",
      auditing: "Opening the page in a browser and checking it at three screen sizes.",
      repairing: "Fixing what the check found.",
      photos: "Finding photographs.",
    },
    steps: {
      studying: "Study the trade",
      writing: "Write the page",
      auditing: "Check it",
      repairing: "Fix it",
      photos: "Add photos",
    },
    elapsed: "{t} elapsed, usually 4 to 6 minutes in total.",
  },
  legal: {
    draft: "Draft. This is the plain-language version, written by us and not yet cleared by counsel. We publish it anyway, because the contract and the marketing must never tell two different stories.",
    updated: "Last updated: 3 September 2026",
    impressum: "Imprint (Impressum)",
    terms: {
      title: "Terms",
      lede: "What you order, what it costs, who owns the finished app, and what happens if something goes wrong.",
      sections: [
        {
          h: "1. Who sells",
          p: [
            "Appwerk is a service of Codemenschen GmbH in Vienna. The full company details are in the imprint.",
            "The contract is between you and Codemenschen GmbH. We build software to order. We broker nothing and we run no marketplace for other providers.",
          ],
        },
        {
          h: "2. What you get",
          p: [
            "We build the app from the brief you write in the wizard, test it, and hand it over finished.",
            "The finished app is yours: code, app and store listing. We keep no share of your revenue, and the same app is not passed on to anyone else.",
          ],
        },
        {
          h: "3. What you pay",
          p: [
            "The development price is fixed before you pay and is due today. Optional packages are listed separately at checkout.",
            "Connected apps run on our servers and carry a monthly hosting and maintenance fee from handover. The amount is shown at checkout, before you pay.",
            "An ad budget is optional, billed separately, and spent 1:1 on ads. It is never part of today's payment.",
          ],
        },
        {
          h: "4. How the build runs",
          p: [
            "The build runs in stages. You get a preview and approve it before anything continues.",
            "Change requests after the preview are part of the plan. How many rounds are included and what a further round costs is shown in your project in the portal, before you start one.",
          ],
        },
        {
          h: "5. Build start and withdrawal",
          p: [
            "As a consumer you have a 14-day right of withdrawal.",
            "You decide at checkout. Either we start right away, in which case the withdrawal right expires when performance begins (§ 18 FAGG). Or we start after the 14 days, in which case it stays fully intact. The box is never pre-ticked.",
            "The details are on the withdrawal page.",
          ],
        },
        {
          h: "6. Advertising",
          p: [
            "Nothing is published before you approve it. Campaigns are created paused, and only your click starts the spending.",
            "You can change the budget monthly or stop it altogether.",
          ],
        },
        {
          h: "7. What we do not promise",
          p: [
            "We promise no revenue, no downloads and no ranking in the store. Figures on this site are estimates and are marked as such.",
            "Apple and Google decide on admission to their stores themselves. We prepare and accompany the submission, we cannot guarantee it.",
          ],
        },
        {
          h: "8. Law",
          p: [
            "Austrian law applies. Mandatory consumer rights in your country of residence remain untouched.",
          ],
        },
      ],
    },
    withdrawal: {
      title: "Withdrawal information",
      lede: "Your 14-day right of withdrawal, and the choice you make yourself at checkout.",
      sections: [
        {
          h: "The right",
          p: [
            "As a consumer in the EU you may withdraw within 14 days without giving any reason. The period starts when the contract is concluded.",
            "An unambiguous statement to office@codemenschen.at before the period ends is enough. We confirm receipt and refund payments received without undue delay.",
          ],
        },
        {
          h: "Your choice at checkout",
          p: [
            "Start now: you expressly request that we begin the build right after payment, and you acknowledge that your withdrawal right expires when performance begins (§ 18 FAGG). The box is never pre-ticked, and you receive the confirmation by e-mail.",
            "Start later: without that tick the build begins after the 14 days and your withdrawal right stays fully intact. Nothing about the app changes, it just starts later.",
          ],
        },
        {
          h: "What is still open",
          p: [
            "The statutory model instruction with its form, and the exact classification of the contract, are with counsel. Until that is settled: in case of doubt we decide in your favour.",
          ],
        },
      ],
    },
  },
  footer: {
    legal: "Terms and withdrawal information are online as a plain-language draft, with counsel review under way. Privacy and imprint follow.",
    by: "Operated by Codemenschen GmbH, Vienna, Austria.",
    imprint: "Imprint (Impressum)",
  },
};
